import decode from 'jwt-decode';
import { omit } from 'lodash';
import queryString from 'query-string';
import { API } from '../api';
import checkVersion from './checkVersion';
import { endpointOverrides } from './endpointOverrides';
import { getApiHostname } from './urlParts';

export { endpointOverrides };

export const getApiConfig = apiName => {
  let config;
  let apiHostname;

  const { hostname, port } = window.location;

  if (hostname === 'localhost') {
    if (API.getSource() === apiName) {
      const apiPort = 5000 + +port;
      apiHostname = `${
        endpointOverrides(process.env)[apiName] || `http://localhost:${apiPort}`
      }/v4/services/${apiName}`;
    } else {
      apiHostname = `${
        endpointOverrides(process.env)[apiName] || 'https://api.develop.commandalkon.io'
      }/v4/services/${apiName}`;
    }

    config = {
      id: apiName,
      hostname: apiHostname,
      apiKey: API.getApiKey(apiName),
    };
  } else {
    const productionHostname = `https://${getApiHostname(hostname, apiName)}/v4/services/${apiName}`;
    config = {
      id: apiName,
      hostname: productionHostname,
      apiKey: API.getApiKey(apiName),
    };
  }

  return config;
};

const getToken = (token, useLoginToken, useEntityToken) => {
  if (token) return token;

  if (useLoginToken) return API.getLoginToken();
  if (useEntityToken) return API.getAppEntityToken()?.value;

  return API.getAppEntityToken()?.value || API.getLoginToken();
};

const handleFetch = async ({
  apiName,
  method,
  path = '',
  data = {},
  queryParams = {},
  token: _token,
  apiKey,
  acceptHeader = 'application/json',
  contentTypeHeader = 'application/json',
  useLoginToken = false,
  useEntityToken = false,
  signal,
  suppressUnauthenticatedOn401 = false,
}) => {
  const query = queryString.stringify(queryParams);

  const apiConfig = getApiConfig(apiName);

  const token = getToken(_token, useLoginToken, useEntityToken);

  try {
    const decodedToken = decode(token);
    const isExpired = decodedToken.exp < Date.now() / 1000;
    if (isExpired) {
      API.onUnauthenticated?.();
      return Promise.resolve(null);
    }
  } catch (e) {
    // ignore
  }

  const allOptions = {
    method,
    headers: {
      Accept: acceptHeader,
      'Content-Type': contentTypeHeader,
      'x-api-key': apiKey || apiConfig?.apiKey,
    },
    body: method === 'GET' ? undefined : JSON.stringify(data),
  };

  if (token) {
    allOptions.headers.Authorization = `Bearer ${token}`;
    allOptions.headers['x-connex-id'] = token;
  }

  const options = { ...allOptions, headers: omit(allOptions.headers, apiConfig.omitHeaders || []) };
  if (signal) options.signal = signal;

  try {
    const response = await fetch(`${apiConfig.hostname}${path}${query ? `?${query}` : ''}`, options);

    // TODO: Consider honoring a return value that bubbles up from the version handler
    //  that indicates whether the response should be processed further.
    checkVersion(apiName, response);

    if (response.ok) {
      return await response.json();
    }

    if (response.status === 401) {
      if (!suppressUnauthenticatedOn401) {
        API.onUnauthenticated?.();
      }

      return suppressUnauthenticatedOn401 ? Promise.reject(response) : Promise.resolve(null);
    }

    if (response.status >= 500) {
      console.error(response);
    }

    return Promise.reject(response);
  } catch (error) {
    if (error?.name === 'AbortError') {
      return;
    }
    console.error(error);
    // API.handleError(error?.issues?.map?.(issue => issue.message)?.join?.(', ') || 'An error has occurred');
  }
};

export const http = {
  post: options => handleFetch({ method: 'POST', ...options }),
  patch: options => handleFetch({ method: 'PATCH', ...options }),
  put: options => handleFetch({ method: 'PUT', ...options }),
  get: options => handleFetch({ method: 'GET', ...options }),
  delete: options => handleFetch({ method: 'DELETE', ...options }),
};
